import { UserBehaviorRisksAppView } from '../view/UserBehaviorRisksAppView';
import { templateUserBehaviorRisksApp } from '../view/template';
import { UserBehaviorRisksAppRepository } from '../repository/UserBehaviorRisksAppRepository';
import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { UserBehaviorRisksAppWebComponent } from '..';
import { ColumnKeys, UserBehaviorRisksAppWebComponentSchema } from '../types';
import {
  getSideNavigationPanelSmartViewsListener,
  getSmartViewsListeners,
} from '../../../grid/genericGridSmartViews/utils';
import { GridEvents } from '../../../grid/types/common';
import { UserBehaviorRisksSideMenuWebComponent } from '../../sideMenus/userBehaviorRisksSideMenu';
import { translateFn } from '../../../utils/translator.helper';
import { SideMenuEvents } from '../../../ui/gzSideMenu/types';
import { removeActiveStateFromGzIconFromAffectedDevices } from '../../common/customRenderers';

import {
  Actions,
  ActionsEvents,
  FULFILLED_PROMISE_STATUS,
  InlineMenuExtraDetailsSelectEvents,
  SelectionEvents,
  DropdownActionsSelection,
} from '../../common/constants';
import {
  createActionDialog,
  renderDialogText,
  RiskManagementSideMenuEvents,
  getUserIdsFromAffectedUsers,
} from '../../sideMenus/common/common';
import {
  handleChangeStateBulkSelectionChanged,
  handleChangeWatchlistBulkSelectionChanged,
  handleComplexEnumFilter,
  handleSelectionChange,
  updateWatchlist,
  handleScan,
  handleRowsLoadingFinishedEvent,
  handlePivotFromExtraDetailsPanelToIncidents,
  handlePivotFromExtraDetailsPanelToAdvancedSearch,
  overlayOpenListener,
  isCloudPlatform,
  buildCategoriesSideMenu,
  processAffectedDeviceData,
  processVulnerableIdentitiesData,
} from '../../common/utils';
import { ColumnKeys as UsersColumnKeys } from '../../usersApp/types';
import { WEB_COMPONENT_USER_BEHAVIOR_RISKS_SIDE_MENU } from '../../../webcomponents.constants';
import { convertGridFiltersToExtJsFilters } from '../../../grid/utils/filters';
import { ConsoleRequest, DataProviderType } from '../../../utils/request';
import { ButtonEvents, GzButtonState } from '../../../ui/gzButton/types';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { ContextMenuOverlayEvents } from '../../../ui/gzContextMenuOverlay/component/gzContextMenuOverlay';
import { NavigationHelper } from '@nirvana/shared/navigation';

export class UserBehaviorRisksAppController extends BaseWebComponentController {
  public component: UserBehaviorRisksAppWebComponent;
  public dataRepository: UserBehaviorRisksAppRepository;
  public view: UserBehaviorRisksAppView;
  public viewConstructor: typeof UserBehaviorRisksAppView;
  public dataRepositoryConstructor: typeof UserBehaviorRisksAppRepository;

  protected selectedItems: any;

  constructor(component: UserBehaviorRisksAppWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: UserBehaviorRisksAppView,
      viewTemplate: templateUserBehaviorRisksApp,
      dataRepositoryConstructor: UserBehaviorRisksAppRepository,
    });
  }

  public computeEventListeners() {
    return [
      ...getSmartViewsListeners(
        this.view.dom.$grid,
        this.view.dom.$gridSmartViews,
        this.view.buildGrid.bind(this.view),
        this.view.getTranslations()
      ),
      ...getSideNavigationPanelSmartViewsListener(this.view.dom.$pushBodyLayout, this.view.dom.$gridSmartViews),
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_CLICKED,
        callback: this.handleRowClicked,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROWS_LOADING_FINISHED,
        callback: handleRowsLoadingFinishedEvent.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.VIEW_USERS_BY_HUMAN_RISKS,
        callback: this.handleViewUsersByHumanRisksEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.IGNORE_HUMAN_RISKS,
        callback: this.handleIgnoreHumanRisksEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.RESTORE_HUMAN_RISKS,
        callback: this.handleRestoreHumanRisksEvent,
      },
      {
        element: this.view.dom.$grid,
        event: RiskManagementSideMenuEvents.IGNORE_HUMAN_RISKS,
        callback: this.handleIgnoreHumanRisksEvent,
      },
      {
        element: this.view.dom.$grid,
        event: RiskManagementSideMenuEvents.RESTORE_HUMAN_RISKS,
        callback: this.handleRestoreHumanRisksEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: ActionsEvents.WATCHLIST_CHANGED,
        callback: this.handleWatchlistChangedEvent,
      },
      {
        element: this.view.dom.$grid,
        event: ActionsEvents.WATCHLIST_CHANGED,
        callback: this.handleWatchlistChangedEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.USER_DETAILS_CHANGED,
        callback: this.handleUserDetailsChangedEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.USER_DETAILS_SHOW_EXTRA_DATA,
        callback: this.handleUserDetailsShowExtraDataEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: RiskManagementSideMenuEvents.USER_EXTRA_DETAILS_CHANGED,
        callback: this.handleUserExtraDetailsChangedEvent,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.EXPORT_CSV,
        callback: this.handleCSVExportEvent,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: handleSelectionChange.bind(this),
      },
      {
        element: this.view.dom.changeStateBulk.$select,
        event: SelectEvents.SELECTION_CHANGED,
        callback: (event: CustomEvent) => {
          handleChangeStateBulkSelectionChanged.call(this, event, 'indicatorId');
        },
      },
      {
        element: this.view.dom.changeWatchlistBulk.$select,
        event: SelectEvents.SELECTION_CHANGED,
        callback: handleChangeWatchlistBulkSelectionChanged.bind(this),
      },
      {
        element: this.view.dom.$scanEmptyStateButton,
        event: ButtonEvents.CLICK,
        callback: handleScan.bind(this),
      },
      {
        element: this.view.dom.scanButton.$button,
        event: ButtonEvents.CLICK,
        callback: handleScan.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: InlineMenuExtraDetailsSelectEvents.viewIncidents,
        callback: handlePivotFromExtraDetailsPanelToIncidents.bind(this),
      },
      {
        element: this.view.dom.$sideMenu,
        event: InlineMenuExtraDetailsSelectEvents.viewEvents,
        callback: handlePivotFromExtraDetailsPanelToAdvancedSearch.bind(this),
      },
      {
        // This was needed in order to modify the pointer-events attribute on body
        // It was being set on none at overlay opening and was preventing
        // automated tests to click on other elements while the overlay was open
        element: this.view.dom.$overlay,
        event: ContextMenuOverlayEvents.OPEN,
        callback: overlayOpenListener,
      },
      {
        element: this.view.dom.$grid,
        event: SelectionEvents.IDENTITY_ICON_CLICK,
        callback: this.handleAffectedUserIconClickedEvent,
      },
      {
        element: this.view.dom.$sideMenu,
        event: SideMenuEvents.CLOSE,
        callback: this.handleRightSideNavClosed,
      },
    ];
  }

  async getDataForView(): Promise<UserBehaviorRisksAppWebComponentSchema> {
    return {
      data: await this.dataRepository.getDataForView(),
      externalFilters: this.component.externalFilters,
    };
  }

  async handleRowClicked(event: CustomEvent) {
    if (!event.detail.rowDetails) {
      // header was clicked, so we do nothing
      return;
    }

    const component = new UserBehaviorRisksSideMenuWebComponent();
    component.item = event.detail.rowDetails;
    this.view.dom.$sideMenu.setMenuContent(component);
    this.view.dom.$sideMenu.openSideMenu();

    if (isCloudPlatform(event.detail.rowDetails.operatingSystem)) {
      component.userDetailsEmptyState = true;
      return;
    }

    let userDetailsDataResponse: null | any = null;

    const { firstUserIdFromAffectedUsers, allUserIds } = getUserIdsFromAffectedUsers(
      event.detail.rowDetails.affectedUsers
    );

    let userDataResponseArrayResult;

    if (firstUserIdFromAffectedUsers) {
      const userDataResponsePromise = ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'RiskManagementUsers',
        method: 'getExtraDetailsForUser',
        payload: {
          companyId: event.detail.rowDetails.company._id,
          userUniqueIdentifier: firstUserIdFromAffectedUsers,
          indicatorId: event.detail.rowDetails.indicatorId,
        },
      });

      [userDataResponseArrayResult] = await Promise.allSettled([userDataResponsePromise]);
    }

    if (userDataResponseArrayResult && userDataResponseArrayResult.status === FULFILLED_PROMISE_STATUS) {
      userDetailsDataResponse = userDataResponseArrayResult.value;
    }

    this.enhanceSideMenuWithUserDetailsData(
      component,
      userDetailsDataResponse,
      firstUserIdFromAffectedUsers,
      allUserIds,
      event
    );
  }

  enhanceSideMenuWithUserDetailsData(
    component,
    userDetailsDataResponse,
    firstUserIdFromAffectedUsers,
    allUsersIds,
    event
  ) {
    if (firstUserIdFromAffectedUsers && userDetailsDataResponse && Object.keys(userDetailsDataResponse).length > 0) {
      component.userInitialDropdownDetails = {
        userId: userDetailsDataResponse['user']['id'],
        userName: userDetailsDataResponse['user']['name'],
        companyId: event.detail.rowDetails.company._id,
        userIds: allUsersIds,
        indicatorId: event.detail.rowDetails.indicatorId,
      };

      component.initialUserDetails = userDetailsDataResponse['specifics'];
      component.userDetails = userDetailsDataResponse['specifics'];
    } else if (!firstUserIdFromAffectedUsers) {
      component.userDetailsEmptyState = true;
    } else {
      component.userDetailsHasErrors = true;
    }
  }

  private async handleUserDetailsChangedEvent(event: CustomEvent) {
    const sideMenuComponent = this.view.dom.$sideMenu.querySelector(
      WEB_COMPONENT_USER_BEHAVIOR_RISKS_SIDE_MENU
    ) as UserBehaviorRisksSideMenuWebComponent;

    if (!sideMenuComponent.userDetailsComponent) {
      return;
    }

    this.setLoadingStateForUserDetails(sideMenuComponent, true);

    try {
      const userDataResponse = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'RiskManagementUsers',
        method: 'getExtraDetailsForUser',
        payload: {
          companyId: event.detail.companyId,
          userUniqueIdentifier: event.detail.userId,
          indicatorId: event.detail.indicatorId,
        },
      });

      this.setLoadingStateForUserDetails(sideMenuComponent, false);

      if (
        userDataResponse &&
        Object.keys(userDataResponse).length > 0 &&
        Object.keys(userDataResponse['specifics']).length > 0
      ) {
        sideMenuComponent.userDetails = userDataResponse['specifics'];
      } else {
        sideMenuComponent.userDetails = {};
      }
    } catch (e) {
      this.setLoadingStateForUserDetails(sideMenuComponent, false);
      this.setErrorStateForUserDetails(sideMenuComponent);
    }
  }

  private setErrorStateForUserDetails(sideMenuComponent) {
    sideMenuComponent.userDetailsComponent.hasErrors = true;
  }

  private setErrorStateForUserExtraDetails(sideMenuComponent) {
    sideMenuComponent.userExtraDetailsComponent.hasErrors = true;
  }

  private async handleUserExtraDetailsChangedEvent(event: CustomEvent) {
    const sideMenuComponent = this.view.dom.$sideMenu.querySelector(
      WEB_COMPONENT_USER_BEHAVIOR_RISKS_SIDE_MENU
    ) as UserBehaviorRisksSideMenuWebComponent;

    if (!sideMenuComponent.userExtraDetailsComponent) {
      return;
    }

    this.setLoadingStateForUserExtraDetails(sideMenuComponent, true);

    try {
      const userDataResponse = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'RiskManagementUsers',
        method: 'getExtraDetailsForUser',
        payload: {
          companyId: event.detail.companyId,
          userUniqueIdentifier: event.detail.userId,
          indicatorId: event.detail.indicatorId,
        },
      });

      if (
        userDataResponse &&
        Object.keys(userDataResponse).length > 0 &&
        Object.keys(userDataResponse['specifics']).length > 0 &&
        userDataResponse['specifics'].hasOwnProperty(event.detail.itemsType)
      ) {
        sideMenuComponent.userExtraDetails = {
          items: userDataResponse['specifics'][event.detail.itemsType],
          itemsType: event.detail.itemsType,
          companyId: event.detail.companyId,
        };
      } else {
        sideMenuComponent.userExtraDetails = {
          items: [],
          itemsType: event.detail.itemsType,
          companyId: event.detail.companyId,
        };
      }

      this.setLoadingStateForUserExtraDetails(sideMenuComponent, false);
    } catch (e) {
      this.setLoadingStateForUserExtraDetails(sideMenuComponent, false);
      this.setErrorStateForUserExtraDetails(sideMenuComponent);
    }
  }

  private setLoadingStateForUserDetails(sideMenuComponent, state) {
    sideMenuComponent.userDetailsComponent.userDetailsDropdown.disabled = state;

    if (state) {
      sideMenuComponent.userDetailsComponent.userInformation = null;
    }
  }

  private setLoadingStateForUserExtraDetails(sideMenuComponent, state) {
    sideMenuComponent.userExtraDetailsComponent.userDetailsDropdown.disabled = state;

    if (state) {
      sideMenuComponent.userExtraDetailsComponent.items = null;
    }
  }

  private async handleUserDetailsShowExtraDataEvent(event: CustomEvent) {
    const sideMenuComponent = this.view.dom.$sideMenu.querySelector(
      WEB_COMPONENT_USER_BEHAVIOR_RISKS_SIDE_MENU
    ) as UserBehaviorRisksSideMenuWebComponent;
    sideMenuComponent.showExtraDetailsPanel = event.detail.showExtraDetailsPanel;

    sideMenuComponent.userExtraDetailsInitialDropdown = event.detail.dropdownDetails;
    sideMenuComponent.userExtraDetails = {
      items: event.detail.items,
      itemsType: event.detail.itemsType,
      companyId: event.detail.dropdownDetails.companyId,
    };
  }

  async handleIgnoreHumanRisksEvent(event: CustomEvent) {
    const { indicatorsIds, companyId } = event.detail;

    this.createIgnoreDialog(indicatorsIds, companyId);
  }

  async handleRestoreHumanRisksEvent(event: CustomEvent) {
    const { indicatorsIds, companyId } = event.detail;

    this.createRestoreDialog(indicatorsIds, companyId);
  }

  private handleViewUsersByHumanRisksEvent(event: CustomEvent) {
    const { indicatorsIds, companyId } = event.detail;
    const params = {
      filters: [
        {
          [UsersColumnKeys.RISKS]: {
            complexEnumFilter: indicatorsIds.map(indicatorId => {
              return {
                value: indicatorId,
              };
            }),
          },
        },
        {
          [UsersColumnKeys.COMPANY]: { exactMatch: companyId },
        },
      ],
    };

    NavigationHelper.navigateTo('/risk-management-identities', params);
  }

  private async handleWatchlistChangedEvent(event: CustomEvent) {
    const { newWatchlist, ids, companyId } = event.detail;
    await this.updateWatchlistStatus(newWatchlist, ids, companyId);
  }

  private handleCSVExportEvent(event: CustomEvent) {
    this.view.dom.$grid.controller?.updateCsvButtonState(
      GzButtonState.IS_LOADING,
      this.dependencies.translateFn('Change the current filter selection to generate a new export.')
    );

    // if you change something here, change in GridDataProvider.getSummary also
    let filters: any[] = convertGridFiltersToExtJsFilters(event.detail.filters);
    const complexEnumFilterColumns = [ColumnKeys.AFFECTED_USERS, ColumnKeys.USER_BEHAVIOR_RISK, ColumnKeys.REGIONS];
    filters = handleComplexEnumFilter(complexEnumFilterColumns, filters);

    const url = app.buildDownloadUrl('UserBehaviorRisks', 'exportItemsAsCsv', null, 'CSV');
    return Application.lib.DownloadManager.download(url, { filters: filters }, true);
  }

  private async updateWatchlistStatus(newWatchlistStatus: number, indicatorsIds: Array<number>, companyId: string) {
    await updateWatchlist(
      {
        ids: indicatorsIds,
        companyId,
        newWatchlist: newWatchlistStatus,
      },
      this.view.dom.$grid,
      this.view.dom.$sideMenu,
      WEB_COMPONENT_USER_BEHAVIOR_RISKS_SIDE_MENU,
      'UserBehaviorRisks'
    );
  }

  private createIgnoreDialog(indicatorsIds: Array<number>, companyId: string) {
    let data: { dialogTitle: string; confirmButtonText: string; dialogBodyMessage: string; toastMessages: any };

    if (indicatorsIds.length === 1) {
      data = {
        dialogBodyMessage: `${renderDialogText(
          translateFn(
            "The selected identity risk will be excluded from the overall risk score calculation for identities, resources, and the company's total risk score. You can reverse this action at any time."
          )
        )}`,
        dialogTitle: translateFn('Ignore risk?'),
        confirmButtonText: translateFn('Ignore risk'),
        toastMessages: {
          success: translateFn('The risk has been ignored. Changes will apply shortly.'),
          error: translateFn('Failed to ignore risk.'),
        },
      };
    } else {
      data = {
        dialogBodyMessage: `${renderDialogText(
          translateFn(
            "The selected identity risk will be excluded from the overall risk score calculation for identities, resources, and the company's total risk score. You can reverse this action at any time."
          )
        )}`,
        dialogTitle: translateFn('Ignore risks?'),
        confirmButtonText: translateFn('Ignore risks'),
        toastMessages: {
          success: translateFn('The risks have been ignored. Changes will apply shortly.'),
          error: translateFn('Failed to ignore risks.'),
        },
      };
    }

    const requestPayload = {
      parameters: {
        actionType: Actions.IGNORE_HUMAN_RISKS,
        indicatorsIds,
        companyId,
      },
    };

    createActionDialog(
      {
        dialogTitle: data.dialogTitle,
        confirmButtonText: data.confirmButtonText,
        dialogBodyMessage: data.dialogBodyMessage,
      },
      {
        grid: this.view.dom.$grid,
        sideMenu: this.view.dom.$sideMenu,
      },
      requestPayload,
      data.toastMessages
    );
  }

  private createRestoreDialog(indicatorsIds: Array<number>, companyId: string) {
    let data: { dialogTitle: string; confirmButtonText: string; dialogBodyMessage: string; toastMessages: any };

    if (indicatorsIds.length === 1) {
      data = {
        dialogBodyMessage: `${renderDialogText(
          translateFn(
            "The selected identity risk will be included in the overall risk score calculation for identities, resources, and the company's total risk score. You can reverse this action at any time."
          )
        )}`,
        dialogTitle: translateFn('Restore ignored risk?'),
        confirmButtonText: translateFn('Restore ignored risk'),
        toastMessages: {
          success: translateFn('The risk has been restored. Changes will apply shortly.'),
          error: translateFn('Failed to restore risk.'),
        },
      };
    } else {
      data = {
        dialogBodyMessage: `${renderDialogText(
          translateFn(
            "The selected identity risks will be included in the overall risk score calculation for identities, resources, and the company's total risk score. You can reverse this action at any time."
          )
        )}`,
        dialogTitle: translateFn('Restore ignored risks?'),
        confirmButtonText: translateFn('Restore ignored risks'),
        toastMessages: {
          success: translateFn('The risks have been restored. Changes will apply shortly.'),
          error: translateFn('Failed to restore risks.'),
        },
      };
    }

    const requestPayload = {
      parameters: {
        actionType: Actions.RESTORE_HUMAN_RISKS,
        indicatorsIds,
        companyId,
      },
    };
    createActionDialog(
      {
        dialogTitle: data.dialogTitle,
        confirmButtonText: data.confirmButtonText,
        dialogBodyMessage: data.dialogBodyMessage,
      },
      {
        grid: this.view.dom.$grid,
        sideMenu: this.view.dom.$sideMenu,
      },
      requestPayload,
      data.toastMessages
    );
  }

  private async handleAffectedUserIconClickedEvent(event: CustomEvent) {
    const { iconType, companyId, affectedIdentitiesIds } = event.detail;

    const actionDetails = [
      {
        title: translateFn('View identity'),
        value: DropdownActionsSelection.VIEW_IDENTITY,
        actionCallback: (categoryType, item, index, companyIdParam) => {
          Application.lib.Navigation.openAppPageInNewTab(
            `/risk-management-identities`,
            {
              filters: [
                {
                  [UsersColumnKeys.USERNAME]: {
                    complexEnumFilter: [{
                      label: item.name,
                      value: item.name
                    }]
                  }
                },
                {
                  [UsersColumnKeys.COMPANY]: { exactMatch: companyIdParam },
                }
              ]
            }
          );
        },
      },
      {
        title: translateFn('View incidents'),
        value: DropdownActionsSelection.VIEW_INCIDENTS,
        actionCallback: (categoryType, item, index, companyIdParam) => {
          Application.lib.Navigation.openInNewTab(
            `/incidents/filter/username/${companyIdParam}&${encodeURIComponent(item.name)}`
          );
        },
      },
    ];

    const affectedIdentitiesSideMenu = buildCategoriesSideMenu(
      companyId,
      iconType,
      translateFn('Vulnerable identities'),
      translateFn('Search identities'),
      actionDetails
    );

    this.view.dom.$sideMenu.setMenuContent(affectedIdentitiesSideMenu);
    this.view.dom.$sideMenu.openSideMenu();

    let typeToItemsMapping = [];
    try {
      typeToItemsMapping = await ConsoleRequest.createRequest<any>({
        dataProviderType: DataProviderType.CORE,
        endpoint: 'UserBehaviorRisks',
        method: 'getVulnerableIdentitiesDetails',
        payload: {
          companyId,
          affectedIdentitiesIds,
        },
      });
    } catch (error) {
      affectedIdentitiesSideMenu.hasErrors = true;
    }

    const categoriesForSidePanel = processVulnerableIdentitiesData(typeToItemsMapping);
    affectedIdentitiesSideMenu.initialCategories = categoriesForSidePanel;
  }

  handleRightSideNavClosed() {
    removeActiveStateFromGzIconFromAffectedDevices(this.view);
  }
}
